// Signage → Tarifario y avails (/signage/tarifario). La capa comercial del
// retail media: (1) tarifario de $/pantalla·día por clasificación del local
// (A/B/C/D auto-computada por ventas), día y franja — gana la regla más
// específica; (2) cotizador de AVAILS: para un rango + franja muestra qué
// pantallas están libres o vendidas, su audiencia auditable (tickets de caja
// por día y dentro de la franja) e impresiones reales, cotiza la inversión con
// CPM efectivo y crea los contratos BORRADOR de un clic.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Calculator, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Campo, Modal, Switch, fieldCls } from '@/features/pos/shared';
import { DIAS, RefRow, sigInsert, sigList, sigRpc, sigUpdate } from './shared';

interface Tarifa {
  id: string; nombre: string; clasificacion: string | null; dia_semana: number | null;
  hora_inicio: string | null; hora_fin: string | null; tarifa_dia: number; es_activo: boolean;
}
interface AvailRow {
  pantalla_id: string; pantalla: string; local: string | null; grupo: string | null;
  clasificacion: string | null; tickets_dia: number; tickets_franja: number; impresiones_dia: number;
  tarifa_dia: number | null; tarifa_nombre: string | null;
  ocupado_por: { contrato: string; anunciante: string | null; estado: string } | null;
}
interface Avails { desde: string; hasta: string; dias: number; hora_inicio: string; hora_fin: string; dia_semana: number | null; pantallas: AvailRow[] }

const money = (n: number) => `$${(Number.isFinite(n) ? n : 0).toLocaleString('es', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const hoy = () => new Date().toISOString().slice(0, 10);
const enDias = (n: number) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);
const CLASES = ['A', 'B', 'C', 'D'];

const tarifaVacia = { nombre: '', clasificacion: '', dia_semana: '' as string, hora_inicio: '', hora_fin: '', tarifa_dia: 10, es_activo: true };

export function Tarifario() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();

  // ---------------- Tarifario ----------------
  const { data: tarifas = [] } = useQuery<Tarifa[]>({
    queryKey: ['sig-tarifas'],
    queryFn: () => sigList<Tarifa>('tarifa?select=*&deleted_at=is.null&order=clasificacion.nullslast,hora_inicio.nullsfirst'),
  });
  const [edit, setEdit] = useState<typeof tarifaVacia & { id?: string } | null>(null);

  const guardarTarifa = useMutation({
    mutationFn: async () => {
      if (!edit?.nombre.trim()) throw new Error('El nombre es obligatorio');
      if (!(Number(edit.tarifa_dia) >= 0)) throw new Error('Tarifa inválida');
      if (!!edit.hora_inicio !== !!edit.hora_fin) throw new Error('Completa las dos horas de la franja (o deja ambas vacías)');
      const body = {
        nombre: edit.nombre.trim(), clasificacion: edit.clasificacion || null,
        dia_semana: edit.dia_semana === '' ? null : Number(edit.dia_semana),
        hora_inicio: edit.hora_inicio || null, hora_fin: edit.hora_fin || null,
        tarifa_dia: Number(edit.tarifa_dia), es_activo: edit.es_activo,
      };
      if (edit.id) await sigUpdate('tarifa', edit.id, body);
      else await sigInsert('tarifa', body);
    },
    onSuccess: () => { toast.success('Tarifa guardada'); setEdit(null); void qc.invalidateQueries({ queryKey: ['sig-tarifas'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const borrarTarifa = (t: Tarifa) =>
    sigUpdate('tarifa', t.id, { deleted_at: new Date().toISOString(), es_activo: false })
      .then(() => { void qc.invalidateQueries({ queryKey: ['sig-tarifas'] }); toast.success('Tarifa eliminada'); })
      .catch((e) => toast.error(getErrorMessage(e)));

  const diaL = (v: number | null) => DIAS.find((d) => d.v === v)?.l ?? 'Todos los días';

  // ---------------- Cotizador ----------------
  const [desde, setDesde] = useState(enDias(1));
  const [hasta, setHasta] = useState(enDias(7));
  const [hIni, setHIni] = useState('11:00');
  const [hFin, setHFin] = useState('14:00');
  const [dia, setDia] = useState<string>('');
  const [avails, setAvails] = useState<Avails | null>(null);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [override, setOverride] = useState<string>('');   // tarifa manual opcional
  const [buscando, setBuscando] = useState(false);

  const consultar = async () => {
    setBuscando(true);
    try {
      const r = await sigRpc<Avails>('sig_avails', {
        _desde: desde, _hasta: hasta, _hora_inicio: hIni, _hora_fin: hFin,
        _dia_semana: dia === '' ? null : Number(dia),
      });
      setAvails(r);
      setSel(new Set(r.pantallas.filter((p) => !p.ocupado_por).map((p) => p.pantalla_id)));
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setBuscando(false); }
  };

  const tarifaDe = (p: AvailRow) => (override !== '' ? Number(override) || 0 : Number(p.tarifa_dia ?? 0));
  const seleccionadas = useMemo(() => (avails?.pantallas ?? []).filter((p) => sel.has(p.pantalla_id)), [avails, sel]);
  const cot = useMemo(() => {
    const dias = avails?.dias ?? 0;
    const inversion = seleccionadas.reduce((s, p) => s + tarifaDe(p) * dias, 0);
    const audiencia = seleccionadas.reduce((s, p) => s + Number(p.tickets_franja) * dias, 0);
    return { dias, inversion, audiencia, cpm: audiencia > 0 ? inversion / (audiencia / 1000) : null };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seleccionadas, avails, override]);

  const toggle = (id: string) => setSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  // ---------------- Contratar ----------------
  const { data: anunciantes = [] } = useQuery<RefRow[]>({
    queryKey: ['sig-anunciantes'],
    queryFn: () => sigList<RefRow>('anunciante?select=id,nombre&deleted_at=is.null&activo=is.true&order=nombre'),
  });
  const [contratar, setContratar] = useState<{ anunciante: string; nombre: string } | null>(null);
  const [creando, setCreando] = useState(false);
  const crearContratos = async () => {
    if (!contratar?.anunciante) { toast.error('Selecciona el anunciante'); return; }
    if (!contratar.nombre.trim()) { toast.error('Ponle nombre a la pauta'); return; }
    setCreando(true);
    try {
      // una llamada por tarifa distinta (la RPC recibe una tarifa por lote de pantallas)
      const grupos = new Map<number, string[]>();
      seleccionadas.forEach((p) => { const t = tarifaDe(p); grupos.set(t, [...(grupos.get(t) ?? []), p.pantalla_id]); });
      let n = 0;
      for (const [tarifa, ids] of grupos) {
        const r = await sigRpc<{ contratos: number }>('sig_cotizacion_contratar', {
          _anunciante: contratar.anunciante, _nombre: contratar.nombre.trim(),
          _desde: desde, _hasta: hasta, _hora_inicio: hIni, _hora_fin: hFin,
          _dia_semana: dia === '' ? null : Number(dia), _pantallas: ids, _tarifa_dia: tarifa,
        });
        n += r.contratos;
      }
      toast.success(`${n} contrato(s) borrador creados — confírmalos en Contratos de franja`);
      setContratar(null);
      void consultar();   // refresca la ocupación
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setCreando(false); }
  };

  return (
    <div className="p-6 space-y-4 max-w-6xl">
      {dlg.ui}
      <h1 className="text-xl font-semibold flex items-center gap-2"><Calculator className="w-5 h-5" /> Tarifario y avails</h1>
      <p className="text-sm text-muted-foreground">
        Define cuánto vale cada pantalla por día según el <b>tipo de local</b> (clasificación A/B/C/D por ventas), día y franja.
        El cotizador muestra la disponibilidad real y la <b>audiencia auditable</b>: tickets de caja del local por día y dentro de la franja.
      </p>

      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between">
          <CardTitle className="text-base">Tarifario ($ por pantalla · día)</CardTitle>
          <Button size="sm" onClick={() => setEdit({ ...tarifaVacia })}><Plus className="w-4 h-4 mr-1" /> Nueva tarifa</Button>
        </CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead><tr className="text-muted-foreground border-b">
              <th className="py-1.5 pr-3 text-left">Regla</th><th className="py-1.5 pr-3 text-center">Local</th>
              <th className="py-1.5 pr-3 text-left">Día</th><th className="py-1.5 pr-3 text-left">Franja</th>
              <th className="py-1.5 pr-3 text-right">$/día</th><th className="py-1.5 text-right">Acciones</th>
            </tr></thead>
            <tbody>
              {tarifas.map((t) => (
                <tr key={t.id} className={`border-b last:border-0 ${t.es_activo ? '' : 'opacity-50'}`}>
                  <td className="py-1.5 pr-3">{t.nombre}</td>
                  <td className="py-1.5 pr-3 text-center">{t.clasificacion ?? 'Todos'}</td>
                  <td className="py-1.5 pr-3">{diaL(t.dia_semana)}</td>
                  <td className="py-1.5 pr-3">{t.hora_inicio ? `${t.hora_inicio.slice(0, 5)}–${t.hora_fin?.slice(0, 5)}` : 'Todo el día'}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums font-medium">{money(Number(t.tarifa_dia))}</td>
                  <td className="py-1.5 text-right">
                    <button className="p-1 text-muted-foreground hover:text-foreground" onClick={() => setEdit({
                      id: t.id, nombre: t.nombre, clasificacion: t.clasificacion ?? '', dia_semana: t.dia_semana == null ? '' : String(t.dia_semana),
                      hora_inicio: t.hora_inicio?.slice(0, 5) ?? '', hora_fin: t.hora_fin?.slice(0, 5) ?? '', tarifa_dia: Number(t.tarifa_dia), es_activo: t.es_activo,
                    })}><Pencil className="w-4 h-4" /></button>
                    <button className="p-1 text-red-500" onClick={async () => { if (await dlg.confirmar(`¿Eliminar "${t.nombre}"?`, { peligro: true })) void borrarTarifa(t); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
              {!tarifas.length && <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">Sin tarifas. Crea al menos una regla general (local "Todos", todo el día) para que el cotizador sugiera precios.</td></tr>}
            </tbody>
          </table>
          <p className="text-xs text-muted-foreground mt-2">Al cotizar gana la regla más específica: clasificación del local &gt; franja horaria &gt; día.</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Cotizador de disponibilidad (avails)</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 items-end">
            <Campo label="Desde"><input type="date" className={fieldCls} value={desde} min={hoy()} onChange={(e) => setDesde(e.target.value)} /></Campo>
            <Campo label="Hasta"><input type="date" className={fieldCls} value={hasta} onChange={(e) => setHasta(e.target.value)} /></Campo>
            <Campo label="Franja desde"><input type="time" className={fieldCls} value={hIni} onChange={(e) => setHIni(e.target.value)} /></Campo>
            <Campo label="Franja hasta"><input type="time" className={fieldCls} value={hFin} onChange={(e) => setHFin(e.target.value)} /></Campo>
            <Campo label="Día de semana">
              <select className={fieldCls} value={dia} onChange={(e) => setDia(e.target.value)}>
                {DIAS.map((d) => <option key={String(d.v)} value={d.v ?? ''}>{d.l}</option>)}
              </select>
            </Campo>
            <Button isLoading={buscando} onClick={() => void consultar()}><Search className="w-4 h-4 mr-1" /> Consultar</Button>
          </div>

          {avails && (
            <>
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead><tr className="text-muted-foreground border-b bg-muted/30">
                    <th className="py-2 px-2 w-8" /><th className="py-2 px-2 text-left">Pantalla</th>
                    <th className="py-2 px-2 text-left">Local</th><th className="py-2 px-2 text-center">Clase</th>
                    <th className="py-2 px-2 text-right">Tickets/día</th><th className="py-2 px-2 text-right">En franja</th>
                    <th className="py-2 px-2 text-right">Impresiones/día</th><th className="py-2 px-2 text-right">Tarifa/día</th>
                    <th className="py-2 px-2 text-left">Estado</th>
                  </tr></thead>
                  <tbody>
                    {avails.pantallas.map((p) => (
                      <tr key={p.pantalla_id} className={`border-b last:border-0 ${p.ocupado_por ? 'opacity-60' : ''}`}>
                        <td className="py-1.5 px-2"><input type="checkbox" disabled={!!p.ocupado_por} checked={sel.has(p.pantalla_id)} onChange={() => toggle(p.pantalla_id)} /></td>
                        <td className="py-1.5 px-2">{p.pantalla}</td>
                        <td className="py-1.5 px-2 text-muted-foreground">{p.local ?? '—'}{p.grupo ? ` · ${p.grupo}` : ''}</td>
                        <td className="py-1.5 px-2 text-center"><span className="inline-block w-6 rounded bg-muted text-center font-semibold">{p.clasificacion ?? '—'}</span></td>
                        <td className="py-1.5 px-2 text-right tabular-nums">{Number(p.tickets_dia).toLocaleString('es')}</td>
                        <td className="py-1.5 px-2 text-right tabular-nums font-medium">{Number(p.tickets_franja).toLocaleString('es')}</td>
                        <td className="py-1.5 px-2 text-right tabular-nums text-muted-foreground">{Number(p.impresiones_dia).toLocaleString('es')}</td>
                        <td className="py-1.5 px-2 text-right tabular-nums">{p.tarifa_dia != null || override !== '' ? money(tarifaDe(p)) : <span className="text-amber-600" title="Sin regla de tarifario que aplique">—</span>}</td>
                        <td className="py-1.5 px-2">
                          {p.ocupado_por
                            ? <span className="text-xs text-red-600">Vendida · {p.ocupado_por.anunciante ?? p.ocupado_por.contrato}</span>
                            : <span className="text-xs text-emerald-600">Disponible</span>}
                        </td>
                      </tr>
                    ))}
                    {!avails.pantallas.length && <tr><td colSpan={9} className="py-6 text-center text-muted-foreground">Sin pantallas activas en modo playlist.</td></tr>}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-wrap items-end gap-4">
                <Campo label="Tarifa manual $/pantalla·día (opcional, pisa la sugerida)" className="w-64">
                  <input type="number" min={0} step="0.01" className={`${fieldCls} text-right`} value={override} placeholder="usar tarifario" onChange={(e) => setOverride(e.target.value)} />
                </Campo>
                <div className="ml-auto grid grid-cols-4 gap-4 text-right text-sm">
                  <div><div className="text-xs text-muted-foreground">Pantallas × días</div><div className="font-semibold tabular-nums">{seleccionadas.length} × {cot.dias}</div></div>
                  <div><div className="text-xs text-muted-foreground">Audiencia en franja</div><div className="font-semibold tabular-nums">{Math.round(cot.audiencia).toLocaleString('es')} tickets</div></div>
                  <div><div className="text-xs text-muted-foreground">CPM efectivo</div><div className="font-semibold tabular-nums">{cot.cpm != null ? money(cot.cpm) : '—'}</div></div>
                  <div><div className="text-xs text-muted-foreground">Inversión</div><div className="text-lg font-bold tabular-nums">{money(cot.inversion)}</div></div>
                </div>
                <Button disabled={!seleccionadas.length} onClick={() => setContratar({ anunciante: '', nombre: '' })}>Crear contrato(s) borrador</Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Audiencia = tickets de caja del local dentro de la franja (últimos 30 días, auditable en el POS). CPM efectivo = inversión por cada
                1.000 tickets expuestos. Los contratos nacen en <b>borrador</b>: el anti-sobreventa corre al confirmarlos en Contratos de franja.
              </p>
            </>
          )}
        </CardContent>
      </Card>

      {edit && (
        <Modal title={edit.id ? 'Editar tarifa' : 'Nueva tarifa'} onClose={() => setEdit(null)}>
          <div className="space-y-3">
            <Campo label="Nombre de la regla"><input className={fieldCls} value={edit.nombre} placeholder="Prime mediodía locales A" onChange={(e) => setEdit({ ...edit, nombre: e.target.value })} /></Campo>
            <div className="grid grid-cols-2 gap-2">
              <Campo label="Clasificación del local">
                <select className={fieldCls} value={edit.clasificacion} onChange={(e) => setEdit({ ...edit, clasificacion: e.target.value })}>
                  <option value="">Todos</option>
                  {CLASES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Campo>
              <Campo label="Día de semana">
                <select className={fieldCls} value={edit.dia_semana} onChange={(e) => setEdit({ ...edit, dia_semana: e.target.value })}>
                  {DIAS.map((d) => <option key={String(d.v)} value={d.v ?? ''}>{d.l}</option>)}
                </select>
              </Campo>
              <Campo label="Franja desde (vacío = todo el día)"><input type="time" className={fieldCls} value={edit.hora_inicio} onChange={(e) => setEdit({ ...edit, hora_inicio: e.target.value })} /></Campo>
              <Campo label="Franja hasta"><input type="time" className={fieldCls} value={edit.hora_fin} onChange={(e) => setEdit({ ...edit, hora_fin: e.target.value })} /></Campo>
            </div>
            <Campo label="Tarifa ($ por pantalla · día)"><input type="number" min={0} step="0.01" className={`${fieldCls} text-right`} value={edit.tarifa_dia} onChange={(e) => setEdit({ ...edit, tarifa_dia: Number(e.target.value) || 0 })} /></Campo>
            <Switch checked={edit.es_activo} onChange={(v) => setEdit({ ...edit, es_activo: v })} label="Activa" />
            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={() => setEdit(null)}>Cancelar</Button>
              <Button isLoading={guardarTarifa.isPending} onClick={() => guardarTarifa.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}

      {contratar && (
        <Modal title="Crear contratos borrador" onClose={() => setContratar(null)}>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {seleccionadas.length} pantalla(s) · {cot.dias} día(s) · franja {hIni}–{hFin} · inversión {money(cot.inversion)}.
              Se crea un contrato borrador por pantalla con su franja; confírmalos en Contratos de franja (ahí corre el anti-sobreventa).
            </p>
            <Campo label="Anunciante">
              <select className={fieldCls} value={contratar.anunciante} onChange={(e) => setContratar({ ...contratar, anunciante: e.target.value })}>
                <option value="">— Selecciona —</option>
                {anunciantes.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
              </select>
            </Campo>
            <Campo label="Nombre de la pauta"><input className={fieldCls} value={contratar.nombre} placeholder="Pauta agosto mediodía" onChange={(e) => setContratar({ ...contratar, nombre: e.target.value })} /></Campo>
            {!anunciantes.length && <p className="text-xs text-amber-700">Primero crea el anunciante en Signage → Anunciantes.</p>}
            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={() => setContratar(null)}>Cancelar</Button>
              <Button isLoading={creando} onClick={() => void crearContratos()}>Crear</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
